import React from "react";
import { CheckIcon } from "@heroicons/react/outline";
import { cn } from "./cn";

/**
 * Bun stats. Spec: Component Library "Cards, panels & stats" + "List rows".
 * Numbers are neutral; color belongs to the label or the delta, sparingly.
 */

/** Bare stat: 26/550 number over a 14 stone label; hero uses 36/600 and a 17 slate label.
 * tone colors the NUMBER only for the rare urgent count (follow-ups due). */
export function Stat({ value, label, tone, size = "default", className }) {
  return (
    <div className={cn("font-instrument", className)}>
      <div
        className={cn(
          "text-bun-text",
          size === "hero" ? "text-bun-balance font-semibold tabular-nums" : "text-bun-number font-strong",
          tone === "danger" && "text-bun-danger-accent"
        )}
      >
        {value}
      </div>
      <div className={cn("font-book", size === "hero" ? "text-bun-heading text-bun-slate mt-1" : "text-bun-meta text-bun-stone mt-[2px]")}>{label}</div>
    </div>
  );
}

/** Horizontal strip of stats separated by hairline dividers (dashboard top). */
export function StatRow({ className, children }) {
  const items = React.Children.toArray(children);
  return (
    <div className={cn("flex items-center gap-6 font-instrument", className)}>
      {items.map((child, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="w-px self-stretch bg-bun-line" />}
          {child}
        </React.Fragment>
      ))}
    </div>
  );
}

/** Bordered stat card: label, big number, optional delta line. */
export function StatCard({ as: Component = "div", label, value, delta, deltaTone = "success", variant = "default", size = "small", allClear = false, indicator, className, ...props }) {
  if (variant === "dashboard") {
    return (
      <Component {...props} type={Component === "button" ? "button" : undefined}
        className={cn("flex w-full min-w-0 flex-col justify-between rounded-bun-lg border px-[14px] py-3 text-left font-instrument",
          size === "large" ? "min-h-[100px]" : "min-h-[78px]",
          allClear ? "border-bun-success-border bg-bun-success-bg" : "border-bun-line bg-bun-surface",
          Component === "button" && "cursor-pointer hover:border-bun-line-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue",
          className)}>
        <div className={cn("line-clamp-2 text-bun-meta font-strong leading-snug", allClear ? "text-bun-success" : "text-bun-stone")}>{label}</div>
        {allClear ? <div className="mt-3 flex items-center gap-2 text-bun-success">
          <CheckIcon className="h-5 w-5 shrink-0 rounded-full bg-bun-success-border p-1" aria-hidden="true" />
          <div><div className="text-bun-body font-semibold">All clear</div>{size === "large" && <div className="text-bun-label font-book">Nothing needs attention</div>}</div>
        </div> : <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
          <span className="text-bun-number font-strong leading-none text-bun-text">{value}</span>
          {indicator}
        </div>}
      </Component>
    );
  }
  return (
    <div className={cn("border border-bun-line rounded-bun-lg px-[18px] py-4 font-instrument", className)}>
      <div className="text-bun-meta font-book text-bun-stone">{label}</div>
      <div className="text-bun-number font-strong text-bun-text mt-1">{value}</div>
      {delta && (
        <div
          className={cn(
            "text-bun-meta font-strong mt-[2px]",
            deltaTone === "success" && "text-bun-success-accent",
            deltaTone === "danger" && "text-bun-danger-accent",
            deltaTone === "neutral" && "text-bun-stone"
          )}
        >
          {delta}
        </div>
      )}
    </div>
  );
}

/** Compact tile: colored caps label, neutral number, meta line (Assets/Debts). */
export function StatTile({
  as: Component = 'div',
  label,
  labelTone = "neutral",
  value,
  meta,
  variant = "default",
  muted = false,
  className,
  ...props
}) {
  const LABEL_TONES = {
    neutral: "text-bun-stone",
    success: "text-bun-success",
    danger: "text-bun-danger",
    info: "text-bun-info",
    special: "text-bun-special",
    warning: "text-bun-warning",
  };
  const compact = variant === "compact";
  const inline = variant === "inline";
  if (variant === 'vital') {
    return (
      <Component {...props} type={Component === 'button' ? 'button' : undefined}
        className={cn('min-h-11 min-w-0 rounded-bun bg-bun-mist px-2.5 py-1.5 text-left font-instrument',
          Component === 'button' && 'cursor-pointer hover:bg-bun-mist-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue disabled:cursor-not-allowed disabled:opacity-50', className)}>
        <div className="text-bun-label font-book uppercase tracking-wide text-bun-stone">{label}</div>
        <div className="text-bun-body font-semibold tabular-nums text-bun-text">{value}</div>
        {meta && <div className="text-bun-label text-bun-stone">{meta}</div>}
      </Component>
    );
  }
  if (variant === 'summary') {
    return (
      <div className={cn('min-w-0 rounded-bun bg-bun-mist px-[14px] py-3 font-instrument', className)}>
        <div className="text-bun-meta font-book text-bun-stone">{label}</div>
        <div className={cn('mt-[3px] break-words text-bun-heading font-semibold', muted ? 'text-bun-stone' : 'text-bun-text')}>{value}</div>
        {meta && <div className="mt-1 text-bun-meta font-book text-bun-stone">{meta}</div>}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "font-instrument",
        inline
          ? "flex h-full min-w-0 flex-col"
          : compact
          ? "rounded-bun-sm bg-bun-surface px-2 py-1.5 text-center"
          : "rounded-bun-lg border border-bun-line px-[14px] py-3",
        className
      )}
    >
      <div
        className={cn(
          "uppercase tracking-[0.06em]",
          inline ? "flex-1 text-bun-label font-book" : "text-[10.5px] font-semibold",
          LABEL_TONES[labelTone]
        )}
      >
        {label}
      </div>
      <div
        className={cn(
          "font-semibold text-bun-text",
          inline ? "mt-0.5 text-bun-body tabular-nums" : compact ? "mt-0.5 text-bun-body" : "mt-[3px] text-[17px]"
        )}
      >
        {value}
      </div>
      {meta && <div className="text-bun-label normal-case tracking-normal font-book text-bun-stone">{meta}</div>}
    </div>
  );
}
